export interface Article {
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  categorySlug: string;
  categoryName: string;
  tags: string[];
  author: string;
  publishedAt: string;
  updatedAt: string;
  readTime: number;
  imageUrl: string;
  featured: boolean;
}

export const articles: Article[] = [
  {
    slug: 'sci-hub-guide-2024',
    title: 'Sci-Hub 最新可用镜像站汇总及使用指南（2024年更新）',
    excerpt: '本文整理了Sci-Hub当前可用的镜像站点，详细介绍使用方法与注意事项，帮助科研人员免费获取学术论文全文。',
    content: `<h2>什么是 Sci-Hub？</h2>
<p>Sci-Hub 是全球最大的免费学术论文获取平台，由哈萨克斯坦程序员 Alexandra Elbakyan 于2011年创立。截至目前，Sci-Hub 收录了超过8500万篇学术论文，覆盖几乎所有学科领域。对于无法通过机构订阅获取论文的研究人员来说，Sci-Hub 是无价之宝。</p>

<h2>2024年最新可用镜像站</h2>
<p>由于版权问题，Sci-Hub 主域名经常被封禁或变更。以下为当前可用的镜像站点（更新时间：2024年7月）：</p>
<ul>
<li>sci-hub.se — 主站，稳定性较高</li>
<li>sci-hub.st — 备用域名</li>
<li>sci-hub.ru — 俄罗斯域名</li>
<li>sci-hub.ee — 爱沙尼亚域名</li>
<li>sci-hub.wf — 瓦利斯和富图纳域名</li>
</ul>

<h2>使用方法</h2>
<p>使用 Sci-Hub 非常简单，只需三步：</p>
<ol>
<li>找到需要下载论文的 DOI（数字对象标识符）或论文标题</li>
<li>打开 Sci-Hub 网站</li>
<li>在搜索框中输入 DOI 或标题，点击搜索</li>
<li>系统会自动返回论文全文 PDF，可直接在线阅读或下载</li>
</ol>

<h2>注意事项</h2>
<p>虽然 Sci-Hub 极大便利了学术研究，但使用时请注意：部分国家/地区可能因法律原因限制访问，建议使用合法合规的方式获取学术资源。</p>`,
    categorySlug: 'free-paper-db',
    categoryName: '免费论文数据库',
    tags: ['Sci-Hub', '论文下载', '学术资源', '免费数据库'],
    author: '学术导航编辑部',
    publishedAt: '2024-07-15',
    updatedAt: '2024-12-20',
    readTime: 8,
    imageUrl: 'https://readdy.ai/api/search-image?query=Academic%20research%20library%20interior%20with%20wooden%20bookshelves%20filled%20with%20journals%20and%20warm%20reading%20lamp%20light%2C%20scholarly%20atmosphere%2C%20knowledge%20repository%2C%20soft%20natural%20lighting%20through%20tall%20windows%2C%20elegant%20academic%20environment&width=800&height=480&seq=sci-hub-guide-2024&orientation=landscape',
    featured: true,
  },
  {
    slug: 'google-scholar-tips',
    title: 'Google Scholar 高级搜索技巧：让文献检索效率提升10倍',
    excerpt: '掌握Google Scholar的高级搜索语法和隐藏功能，快速定位高质量学术文献，打造个性化文献追踪系统。',
    content: `<h2>Google Scholar 简介</h2>
<p>Google Scholar（谷歌学术）是全球最流行的学术搜索引擎，索引了海量学术文献，包括期刊论文、学位论文、图书、会议论文、预印本等多种类型的学术资料。掌握其高级搜索技巧，可以极大提升文献检索效率。</p>

<h2>高级搜索语法</h2>
<h3>1. 精确短语搜索</h3>
<p>使用双引号包围短语进行精确匹配：<code>"machine learning"</code></p>

<h3>2. 作者搜索</h3>
<p>使用 author: 操作符限定作者：<code>author:"Geoffrey Hinton"</code></p>

<h3>3. 标题搜索</h3>
<p>使用 intitle: 限定标题关键词：<code>intitle:"deep learning"</code></p>

<h3>4. 出版物限定</h3>
<p>使用 source: 操作符指定来源：<code>source:"Nature"</code></p>

<h3>5. 时间范围</h3>
<p>在左侧边栏中选择自定义年份范围，或使用高级搜索界面设置</p>

<h3>6. 布尔运算符</h3>
<p>使用 AND、OR、NOT 进行组合搜索，用括号分组复杂查询</p>

<h2>文献追踪系统搭建</h2>
<p>通过 Google Scholar 的"创建快讯"功能，输入关键词后即可定期收到相关新文献的邮件推送。建议针对不同研究方向创建多个快讯，实现自动化文献追踪。</p>`,
    categorySlug: 'free-paper-db',
    categoryName: '免费论文数据库',
    tags: ['Google Scholar', '文献检索', '搜索技巧', '学术工具'],
    author: '学术导航编辑部',
    publishedAt: '2024-08-22',
    updatedAt: '2024-11-15',
    readTime: 10,
    imageUrl: 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20digital%20workspace%20with%20laptop%20displaying%20academic%20search%20results%2C%20clean%20desk%20setup%20with%20coffee%20mug%2C%20warm%20ambient%20light%2C%20scholarly%20productivity%20concept%2C%20soft%20bokeh%20background&width=800&height=480&seq=google-scholar-tips&orientation=landscape',
    featured: true,
  },
  {
    slug: 'zotero-complete-guide',
    title: 'Zotero 文献管理完全指南：从入门到精通',
    excerpt: '从安装配置到高级应用，全面掌握Zotero这款免费开源的文献管理工具，让论文写作更高效。',
    content: `<h2>为什么选择 Zotero？</h2>
<p>Zotero 是一款免费、开源的文献管理工具，由乔治梅森大学的历史学家开发。它支持 Windows、macOS、Linux 全平台，提供浏览器插件实现一键抓取文献信息，是学术写作的得力助手。</p>

<h2>安装与初始配置</h2>
<p>Zotero 的安装非常简单：访问 zotero.org 下载对应操作系统的安装包，同时安装 Zotero Connector 浏览器插件。首次使用时，建议注册 Zotero 账号以启用云同步功能。</p>

<h2>核心功能详解</h2>
<h3>1. 文献抓取</h3>
<p>浏览学术网页时，点击浏览器工具栏的 Zotero 图标即可自动识别并抓取文献元数据，包括标题、作者、发布日期、DOI等。</p>

<h3>2. 文献组织</h3>
<p>支持创建多级文件夹分类、添加标签、设置笔记和附件。可以通过拖拽快速整理文献库。</p>

<h3>3. 引用插入</h3>
<p>在 Word 或 Google Docs 中安装 Zotero 插件后，可以一键插入引用和生成参考文献列表，支持 APA、MLA、Chicago 等数千种引文格式。</p>

<h3>4. PDF 管理</h3>
<p>自动重命名PDF文件、提取注释和高亮、全文搜索等高级功能让文献阅读更加高效。</p>

<h2>高级技巧</h2>
<p>配合 Better BibTeX 插件，Zotero 可以与 LaTeX 无缝集成。使用 ZotFile 插件可以实现PDF文件的自动同步与平板阅读。</p>`,
    categorySlug: 'writing-tools',
    categoryName: '学术写作辅助',
    tags: ['Zotero', '文献管理', '论文写作', '参考文献'],
    author: '学术导航编辑部',
    publishedAt: '2024-06-10',
    updatedAt: '2024-10-05',
    readTime: 12,
    imageUrl: 'https://readdy.ai/api/search-image?query=Organized%20academic%20workspace%20with%20reference%20management%20software%20on%20screen%2C%20stacks%20of%20neatly%20arranged%20research%20papers%20on%20wooden%20desk%2C%20warm%20study%20environment%2C%20modern%20minimalist%20scholarly%20setting&width=800&height=480&seq=zotero-complete-guide&orientation=landscape',
    featured: false,
  },
  {
    slug: 'arxiv-preprint-platform',
    title: 'arXiv 预印本平台深度使用指南',
    excerpt: '了解全球最大预印本平台 arXiv 的完整使用攻略，掌握最新科研动态，第一时间获取前沿研究成果。',
    content: `<h2>arXiv 简介</h2>
<p>arXiv（发音为"archive"）由康奈尔大学运营，是全球最大的学术预印本平台，涵盖物理学、数学、计算机科学、生物学、经济学等众多学科。自1991年创立以来，arXiv 已成为科研人员发布和获取前沿研究成果的最重要平台之一。</p>

<h2>核心学科领域</h2>
<ul>
<li>物理学（hep-th, gr-qc, quant-ph 等）</li>
<li>数学（math.AG, math.NT, math.PR 等）</li>
<li>计算机科学（cs.AI, cs.CL, cs.CV, cs.LG 等）</li>
<li>数量生物学（q-bio）</li>
<li>数量金融（q-fin）</li>
<li>统计学（stat.ML, stat.AP 等）</li>
<li>电气工程（eess）</li>
<li>经济学（econ）</li>
</ul>

<h2>高效使用技巧</h2>
<h3>1. RSS 订阅</h3>
<p>每个学科分类都有对应的 RSS 源，可以通过 RSS 阅读器订阅特定领域的最新论文。格式：https://rss.arxiv.org/rss/cs.AI</p>

<h3>2. 邮件推送</h3>
<p>注册 arXiv 账号后，可以设置每日邮件推送，按学科分类接收最新提交的论文列表。</p>

<h3>3. 第三方工具</h3>
<p>推荐使用 arxiv-sanity、Semantic Scholar 等工具对 arXiv 论文进行筛选和排序，按热门程度、社交推荐等多维度发现高质量论文。</p>`,
    categorySlug: 'open-access',
    categoryName: '开源学术平台',
    tags: ['arXiv', '预印本', '开源学术', '论文获取'],
    author: '学术导航编辑部',
    publishedAt: '2024-09-05',
    updatedAt: '2024-12-01',
    readTime: 9,
    imageUrl: 'https://readdy.ai/api/search-image?query=Digital%20abstract%20visualization%20of%20scientific%20papers%20flowing%20through%20network%20connections%2C%20modern%20tech%20aesthetic%20with%20clean%20lines%20and%20scholarly%20warm%20tones%2C%20preprint%20platform%20concept%20art&width=800&height=480&seq=arxiv-preprint-platform&orientation=landscape',
    featured: true,
  },
  {
    slug: 'research-rabbit-tool',
    title: 'ResearchRabbit：AI驱动的文献发现神器',
    excerpt: '介绍ResearchRabbit这款基于人工智能的文献探索工具，通过可视化方式帮你快速建立研究领域的知识图谱。',
    content: `<h2>ResearchRabbit 是什么？</h2>
<p>ResearchRabbit 是一款创新的 AI 驱动文献发现工具，被用户亲切地称为"学术界的 Spotify"。它通过分析论文之间的引用关系、作者网络和主题相似度，以可视化的方式帮助研究人员发现相关文献，构建个人研究知识图谱。</p>

<h2>核心特色功能</h2>
<h3>1. 种子论文扩散</h3>
<p>只需提供一篇或多篇"种子论文"，ResearchRabbit 就能自动为你推荐相关文献，包括引用这篇论文的后续研究、这篇论文引用的基础文献，以及内容相似的平行研究。</p>

<h3>2. 可视化探索图</h3>
<p>以交互式网络图展示论文之间的关系，支持按时间线、作者、主题等多维度筛选和浏览，直观了解某个研究领域的发展脉络。</p>

<h3>3. 个性化合集</h3>
<p>可以将感兴趣的论文保存到不同的合集中，系统会根据合集中的论文持续推荐新文献，形成正向反馈循环。</p>

<h3>4. 作者网络追踪</h3>
<p>可视化展示某个研究领域内的核心研究者及其合作关系，帮助你快速了解学术圈的关键人物和团队。</p>

<h2>与Zotero集成</h2>
<p>ResearchRabbit 支持与 Zotero 文献库的导入导出，可以在两个工具之间无缝切换，形成完整的文献管理工作流。</p>`,
    categorySlug: 'download-tools',
    categoryName: '文献下载工具',
    tags: ['ResearchRabbit', 'AI工具', '文献发现', '知识图谱'],
    author: '学术导航编辑部',
    publishedAt: '2024-10-18',
    updatedAt: '2024-12-10',
    readTime: 7,
    imageUrl: 'https://readdy.ai/api/search-image?query=Abstract%20digital%20visualization%20of%20connected%20knowledge%20nodes%20forming%20a%20research%20network%20graph%2C%20modern%20data%20science%20aesthetic%20with%20warm%20and%20cool%20tone%20balance%2C%20network%20analysis%20concept&width=800&height=480&seq=research-rabbit-tool&orientation=landscape',
    featured: false,
  },
  {
    slug: 'cnki-alternatives',
    title: '知网替代方案：7个免费中文学术资源平台推荐',
    excerpt: '盘点可替代中国知网的免费中文学术资源平台，帮助国内科研人员降低文献获取成本。',
    content: `<h2>为什么需要知网替代方案？</h2>
<p>中国知网(CNKI)是中国最大的中文学术资源库，但其高昂的访问费用和个人用户的下载限制，使得许多独立研究者、中小机构和在校学生面临文献获取困难。好在，我国正在大力推动学术资源开放获取，以下平台提供了优质的免费替代方案。</p>

<h2>七大免费中文学术平台</h2>

<h3>1. 国家哲学社会科学学术期刊数据库（NSSD）</h3>
<p>由中国社会科学院图书馆建设，收录超过1000种哲学社会科学期刊，全部免费开放获取。</p>

<h3>2. 国家科技图书文献中心（NSTL）</h3>
<p>国家级的科技文献信息保障系统，提供期刊论文、学位论文、会议论文等多种文献类型的免费检索和部分全文服务。</p>

<h3>3. 中国科学院科技论文预印本平台（ChinaXiv）</h3>
<p>国内首个按国际通行模式运营的预印本平台，覆盖多个自然科学学科，所有论文免费开放获取。</p>

<h3>4. 中国科技论文在线</h3>
<p>由教育部科技发展中心主办，提供首发论文、学者专栏、科技期刊等多种资源。</p>

<h3>5. 万方数据知识服务平台（部分免费）</h3>
<p>万方旗下部分资源可免费获取，尤其是一些开放获取期刊和学位论文。</p>

<h3>6. 百度学术</h3>
<p>聚合了海量中英文学术资源，提供免费检索服务，部分文献可直接链接到免费全文。</p>

<h3>7. 全国图书馆参考咨询联盟</h3>
<p>由多家图书馆联合提供文献传递服务，可通过邮箱免费获取所需文献。</p>`,
    categorySlug: 'free-paper-db',
    categoryName: '免费论文数据库',
    tags: ['知网替代', '中文学术', '免费资源', '文献获取'],
    author: '学术导航编辑部',
    publishedAt: '2024-11-02',
    updatedAt: '2024-12-18',
    readTime: 11,
    imageUrl: 'https://readdy.ai/api/search-image?query=Modern%20Chinese%20library%20interior%20with%20digital%20screens%20displaying%20academic%20resources%2C%20clean%20minimalist%20design%20with%20warm%20wood%20tones%2C%20scholarly%20atmosphere%2C%20bright%20natural%20light&width=800&height=480&seq=cnki-alternatives&orientation=landscape',
    featured: true,
  },
  {
    slug: 'latex-beginners',
    title: 'LaTeX 零基础入门：从安装到写出第一篇论文',
    excerpt: '面向完全零基础用户的LaTeX教程，手把手教你搭建写作环境并完成第一篇学术论文排版。',
    content: `<h2>LaTeX 是什么？</h2>
<p>LaTeX 是由 Leslie Lamport 开发的一套基于 TeX 的排版系统，被广泛应用于学术论文、书籍和技术文档的排版。与 Word 等"所见即所得"的编辑器不同，LaTeX 采用"所想即所得"的理念，你只需关注内容和结构，排版由系统自动完成。</p>

<h2>为什么科研人员应该使用 LaTeX？</h2>
<ul>
<li>专业的数学公式排版，效果远超 Word</li>
<li>自动处理参考文献编号和交叉引用</li>
<li>模板丰富，大多数期刊提供官方 LaTeX 模板</li>
<li>纯文本格式，支持版本控制（Git）</li>
<li>免费开源，跨平台兼容</li>
</ul>

<h2>快速入门步骤</h2>
<h3>第一步：安装 TeX 发行版</h3>
<p>推荐 TeX Live（跨平台），Windows 用户也可选 MiKTeX。macOS 用户可使用 MacTeX。</p>

<h3>第二步：选择编辑器</h3>
<p>推荐 VS Code + LaTeX Workshop 插件（免费），或 TeXstudio（免费），Overleaf 是优秀的在线编辑器。</p>

<h3>第三步：第一个文档</h3>
<p>创建一个 .tex 文件，输入基本的文档结构，编译生成 PDF 即可。</p>

<h2>常用学习资源</h2>
<p>推荐阅读《一份不太简短的 LaTeX2e 介绍》（lshort-zh-cn），中文社区活跃于 CTEX 论坛和 LaTeX 工作室。</p>`,
    categorySlug: 'writing-tools',
    categoryName: '学术写作辅助',
    tags: ['LaTeX', '论文排版', '学术写作', '入门教程'],
    author: '学术导航编辑部',
    publishedAt: '2024-07-28',
    updatedAt: '2024-11-20',
    readTime: 10,
    imageUrl: 'https://readdy.ai/api/search-image?query=Clean%20academic%20workspace%20with%20code%20editor%20displaying%20mathematical%20formulas%2C%20minimalist%20desk%20setup%20with%20warm%20lamp%20light%2C%20scholarly%20coding%20environment%2C%20modern%20academic%20atmosphere&width=800&height=480&seq=latex-beginners&orientation=landscape',
    featured: false,
  },
  {
    slug: 'nature-science-free',
    title: '如何免费阅读 Nature、Science 等顶级期刊论文',
    excerpt: '揭秘多种合法免费获取Nature、Science、Cell等顶刊论文的方法和渠道。',
    content: `<h2>顶级期刊的开放获取趋势</h2>
<p>随着开放科学运动的推进，Nature、Science、Cell 等顶级期刊也在逐步扩大开放获取（Open Access）的比例。了解这些渠道，可以帮助你合法、免费地获取最新科研成果。</p>

<h2>合法免费获取渠道</h2>

<h3>1. 作者预印本</h3>
<p>许多期刊允许作者在投稿前或接受后将手稿上传至预印本平台。可在 arXiv、bioRxiv、Research Square 等平台搜索顶刊论文的预印本版本。</p>

<h3>2. 机构知识库</h3>
<p>作者所在大学或研究机构的知识库中可能存储了论文的接受版本。Harvard DASH、MIT DSpace 等是典型代表。</p>

<h3>3. 作者个人主页</h3>
<p>许多研究者会在个人主页或 ResearchGate 上分享论文 PDF。</p>

<h3>4. 开放获取文章</h3>
<p>Nature Communications、Science Advances 等子刊为纯 OA 期刊。Nature 和 Science 本身也有部分 OA 文章。</p>

<h3>5. 期刊促销活动</h3>
<p>部分期刊会在特定时期提供免费访问，关注期刊官方社交媒体获取最新资讯。</p>

<h3>6. Unpaywall 插件</h3>
<p>安装浏览器插件后，浏览论文页面时会自动检测是否有合法的免费版本。</p>`,
    categorySlug: 'free-paper-db',
    categoryName: '免费论文数据库',
    tags: ['Nature', 'Science', '顶级期刊', '免费获取'],
    author: '学术导航编辑部',
    publishedAt: '2024-08-30',
    updatedAt: '2024-12-05',
    readTime: 9,
    imageUrl: 'https://readdy.ai/api/search-image?query=Elegant%20academic%20library%20interior%20with%20prestigious%20scientific%20journals%20displayed%20on%20wooden%20shelves%2C%20warm%20lighting%20creating%20scholarly%20atmosphere%2C%20sophisticated%20research%20environment&width=800&height=480&seq=nature-science-free&orientation=landscape',
    featured: false,
  },
  {
    slug: 'github-student-pack',
    title: 'GitHub 学生开发包：价值20万的学生免费资源汇总',
    excerpt: '全面解读GitHub Student Developer Pack，盘点其中对学术研究最有价值的免费工具和服务。',
    content: `<h2>什么是 GitHub Student Developer Pack？</h2>
<p>GitHub 学生开发包（GitHub Student Developer Pack）是 GitHub 联合多家科技公司为学生提供的一项福利计划。只需使用学校邮箱验证学生身份，即可免费获得价值数十万美元的开发工具和服务。</p>

<h2>学术研究最有价值的免费资源</h2>

<h3>1. GitHub Pro（免费）</h3>
<p>无限私有仓库、GitHub Copilot AI 编程助手（需单独申请）、GitHub Pages 等。</p>

<h3>2. DigitalOcean（$200额度）</h3>
<p>云服务器额度，可用于搭建个人网站、部署研究项目、运行数据分析等。</p>

<h3>3. JetBrains 全家桶（免费）</h3>
<p>PyCharm、IntelliJ IDEA 等专业 IDE 免费使用，对编程类研究极大助力。</p>

<h3>4. Notion Personal Pro（免费）</h3>
<p>强大的知识管理工具，适合搭建研究笔记系统和项目管理。</p>

<h3>5. Canva Pro（免费）</h3>
<p>专业设计工具，制作学术海报、汇报PPT、论文配图等。</p>

<h3>6. Namecheap 域名（免费一年）</h3>
<p>.me 域名免费一年，可搭建个人学术主页。</p>

<h3>7. Termius（免费）</h3>
<p>SSH 客户端，方便远程管理服务器。</p>

<h2>申请条件</h2>
<p>年满13周岁的在读学生，拥有学校发放的 .edu 邮箱或其他可验证的学生身份证明。</p>`,
    categorySlug: 'research-benefits',
    categoryName: '科研福利资源',
    tags: ['GitHub', '学生福利', '开发工具', '免费资源'],
    author: '学术导航编辑部',
    publishedAt: '2024-09-20',
    updatedAt: '2024-12-15',
    readTime: 8,
    imageUrl: 'https://readdy.ai/api/search-image?query=Modern%20student%20workspace%20setup%20with%20laptop%20and%20multiple%20screens%20showing%20code%20and%20development%20tools%2C%20clean%20desk%20with%20plants%2C%20bright%20natural%20light%2C%20productive%20academic%20atmosphere&width=800&height=480&seq=github-student-pack&orientation=landscape',
    featured: false,
  },
  {
    slug: 'connected-papers-visual',
    title: 'Connected Papers：学术论文关系图谱可视化工具',
    excerpt: '利用Connected Papers快速理解学术论文之间的引用关系，发现相关研究，构建文献综述框架。',
    content: `<h2>Connected Papers 简介</h2>
<p>Connected Papers 是一款独特的学术论文可视化探索工具。它不像传统搜索引擎那样按关键词匹配，而是通过分析论文之间的引用和相似度关系，构建一个交互式学术地图，让你直观地看到某篇论文在整个研究领域中的位置。</p>

<h2>核心功能</h2>

<h3>1. 论文关系图谱</h3>
<p>输入一篇论文的 DOI、URL 或标题，系统会生成一个包含约50篇相关论文的关系图谱。节点代表论文，连线代表引用关系，节点距离表示内容相似度。</p>

<h3>2. 先前工作（Prior Works）</h3>
<p>一键查看该领域的重要奠基性工作，帮助你快速了解研究背景和发展脉络。</p>

<h3>3. 衍生工作（Derivative Works）</h3>
<p>查看引用了该论文的后续研究，追踪该工作的学术影响力。</p>

<h2>适用场景</h2>
<ul>
<li>进入新研究领域时的文献调研</li>
<li>撰写文献综述时的论文筛选</li>
<li>追踪某篇重要论文的学术影响</li>
<li>发现被遗漏的相关研究</li>
<li>寻找潜在的跨学科研究灵感</li>
</ul>

<h2>使用技巧</h2>
<p>建议从该领域的经典综述论文开始探索，能获得最全面的领域图谱。多篇论文的图谱可以并排对比，发现不同研究方向的交叉点。</p>`,
    categorySlug: 'download-tools',
    categoryName: '文献下载工具',
    tags: ['Connected Papers', '文献可视化', '引用分析', '研究工具'],
    author: '学术导航编辑部',
    publishedAt: '2024-10-05',
    updatedAt: '2024-12-08',
    readTime: 6,
    imageUrl: 'https://readdy.ai/api/search-image?query=Abstract%20network%20visualization%20of%20connected%20academic%20papers%20forming%20a%20knowledge%20graph%2C%20modern%20data%20visualization%20aesthetic%20with%20warm%20amber%20nodes%20and%20clean%20connections%2C%20scholarly%20concept%20art&width=800&height=480&seq=connected-papers-visual&orientation=landscape',
    featured: false,
  },
  {
    slug: 'usage-guide',
    title: '第一学术福利导航网使用指南',
    excerpt: '全面了解第一学术福利导航网的功能和使用方法，快速上手高效获取学术资源。',
    content: `<h2>欢迎使用第一学术福利导航网</h2>
<p>第一学术福利导航网致力于为科研人员和高校师生提供一站式的免费学术资源导航服务。本指南将帮助你全面了解网站功能，快速找到所需资源。</p>

<h2>网站结构概览</h2>
<h3>首页导航</h3>
<p>首页汇集了所有核心功能入口，包括热门推荐、分类导航、最新文章等模块。顶部导航栏提供各大分类的快速入口。</p>

<h3>分类浏览</h3>
<p>资源按六大类别组织：免费论文数据库、文献下载工具、学术期刊导航、科研福利资源、开源学术平台、学术写作辅助。点击任意分类即可查看该类别下的所有资源。</p>

<h3>文章阅读</h3>
<p>每篇文章详细介绍一个学术资源或工具的使用方法、技巧和注意事项。文章页面提供面包屑导航，方便返回上级页面。</p>

<h2>如何反馈与贡献</h2>
<p>如果你知道未被收录的优秀学术资源，欢迎通过关于我们页面的联系方式向我们推荐。我们持续更新资源库，确保信息的时效性和准确性。</p>`,
    categorySlug: 'free-paper-db',
    categoryName: '免费论文数据库',
    tags: ['使用指南', '导航', '帮助'],
    author: '学术导航编辑部',
    publishedAt: '2024-01-01',
    updatedAt: '2024-12-01',
    readTime: 5,
    imageUrl: 'https://readdy.ai/api/search-image?query=Clean%20modern%20library%20navigation%20concept%20with%20digital%20wayfinding%20system%2C%20minimalist%20interior%20with%20warm%20lighting%2C%20organized%20resource%20directory%20aesthetic%2C%20contemporary%20academic%20setting&width=800&height=480&seq=usage-guide&orientation=landscape',
    featured: false,
  },
  {
    slug: 'faq',
    title: '常见问题解答',
    excerpt: '关于第一学术福利导航网的常见问题汇总，帮你快速找到答案。',
    content: `<h2>常见问题</h2>

<h3>Q1：网站上的资源都是免费的吗？</h3>
<p>是的，我们收录的所有资源在基本使用层面都是免费的。部分工具和平台可能提供付费高级功能，但核心功能均可免费使用。</p>

<h3>Q2：如何确定推荐的资源是否可靠？</h3>
<p>我们团队的编辑人员会对每个资源进行实际测试和评估，确保推荐的资源安全、有效、可靠。</p>

<h3>Q3：网站多久更新一次？</h3>
<p>我们每周都会检查和更新资源列表，确保链接有效、信息准确。重大更新会通过网站公告通知用户。</p>

<h3>Q4：可以向你们推荐学术资源吗？</h3>
<p>当然可以！请通过关于我们页面的联系表单提交推荐，我们的编辑团队会进行审核评估。</p>

<h3>Q5：网站是否需要注册？</h3>
<p>不需要。第一学术福利导航网完全免费开放，无需注册即可浏览所有内容。</p>`,
    categorySlug: 'free-paper-db',
    categoryName: '免费论文数据库',
    tags: ['FAQ', '常见问题', '帮助'],
    author: '学术导航编辑部',
    publishedAt: '2024-02-15',
    updatedAt: '2024-11-28',
    readTime: 4,
    imageUrl: 'https://readdy.ai/api/search-image?query=Clean%20modern%20help%20desk%20concept%20with%20organized%20information%20display%2C%20minimalist%20workspace%20with%20warm%20lighting%2C%20knowledge%20base%20aesthetics%2C%20contemporary%20customer%20service%20environment&width=800&height=480&seq=faq&orientation=landscape',
    featured: false,
  },
];

export const articleMap: Record<string, Article> = {};
articles.forEach((a) => {
  articleMap[a.slug] = a;
});

export const articlesByCategory: Record<string, Article[]> = {};
articles.forEach((a) => {
  if (!articlesByCategory[a.categorySlug]) {
    articlesByCategory[a.categorySlug] = [];
  }
  articlesByCategory[a.categorySlug].push(a);
});

export const featuredArticles = articles.filter((a) => a.featured);
export const latestArticles = [...articles].sort(
  (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
);